import axios from 'axios';

const TOKEN_KEY = 'sc2_token';
const USER_KEY = 'sc2_user';

const api = axios.create({
    baseURL: '/api',
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json'
    }
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Standardize response errors and handle auth expiration globally
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error && error.response) {
            const status = error.response.status;
            const serverMsg = error.response.data?.msg;
            // If unauthorized, clear stored user and token so UI can react
            if (status === 401) {
                clearUser();
            }
            // Normalize message for downstream catch blocks
            error.message = serverMsg || error.response.statusText || `HTTP ${status}`;
        } else {
            error.message = error.message || '网络错误，请重试';
        }
        return Promise.reject(error);
    }
);

// ============ User APIs ============

export function register(data) {
    return api.post('/user/register', data);
}

export function login(data) {
    return api.post('/user/login', data);
}

export function loginByCode(email, code) {
    return api.post('/user/login/code', null, { params: { email, code } });
}

export function resetPassword(email, code, newPassword) {
    return api.post('/user/password/reset', null, { params: { email, code, newPassword } });
}

export function sendEmailCode(email, requireRegistered = false) {
    return api.post('/email/send', null, { params: { email, requireRegistered } });
}

export function getUserProfile(userId) {
    return api.get(`/user/${userId}`);
}

export function updateProfile(userId, data) {
    return api.put(`/user/${userId}/profile`, data);
}

export function uploadAvatar(userId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/user/${userId}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
}

export function setPresetAvatar(userId, preset) {
    return api.put(`/user/${userId}/avatar/preset`, null, { params: { preset } });
}

export function findMatches(mmr, range, race, mode, minLevel) {
    const params = { mmr, range };
    if (race) params.race = race;
    if (mode) params.mode = mode;
    if (minLevel !== undefined && minLevel !== null) params.minLevel = minLevel;
    return api.get('/user/match', { params });
}

// ============ Cheater APIs ============

export function reportCheater(data, userId) {
    return api.post('/cheater/report', data, { params: { userId } });
}

export function getCheaterList() {
    return api.get('/cheater/list');
}

export function searchCheaters(battleTag) {
    return api.get('/cheater/search', { params: { battleTag } });
}

// ============ Tutorial APIs ============

export function getTutorials(category) {
    const params = {};
    if (category) params.category = category;
    return api.get('/tutorial/list', { params });
}

export function createTutorial(data) {
    return api.post('/tutorial', data);
}

// ============ Event APIs ============

export function getEvents() {
    return api.get('/event/list');
}

export function createEvent(data, userId) {
    return api.post('/event', data, { params: { userId } });
}

// ============ SC2 Pulse APIs ============

export function searchSC2Players(name) {
    return api.get('/sc2/search', { params: { name } });
}

export function getSC2MMR(characterId, queue = 'LOTV_1V1') {
    return api.get(`/sc2/mmr/${characterId}`, { params: { queue } });
}

export function getSC2Streams() {
    return api.get('/sc2/streams');
}

// ============ Stream APIs (User-added) ============

export function addStream(data) {
    return api.post('/stream', data);
}

export function listUserStreams() {
    return api.get('/stream/list');
}

export function deleteStream(id, adminId) {
    return api.delete(`/stream/${id}`, { params: { adminId } });
}

// ============ Public Reports (挂人区) APIs ============

export function getPublicReports(search) {
    const params = {};
    if (search) params.search = search;
    return api.get('/public-report/list', { params });
}

export function createPublicReport(data) {
    return api.post('/public-report', data);
}

export function uploadReportImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/public-report/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
}

// ============ Feedback APIs ============

export function createFeedback(data) {
    return api.post('/feedback', data);
}

export function getMyFeedbacks() {
    return api.get('/feedback/my');
}

export function getResolvedFeedbacks() {
    return api.get('/feedback/resolved');
}

export function getPublicFeedbacks() {
    return api.get('/feedback/public');
}

// ============ QQ Group APIs ============

export function getQqGroups(offset = 0, limit = 30, keyword = '') {
    const params = { offset, limit };
    if (keyword) params.keyword = keyword;
    return api.get('/qq-group/list', { params });
}

export function createQqGroup(data) {
    return api.post('/qq-group', data);
}

export function deleteQqGroup(id) {
    return api.delete(`/qq-group/${id}`);
}

// ============ Coaching APIs ============

export function getCoachingPosts(type) {
    const params = {};
    if (type && type !== 'all') params.type = type;
    return api.get('/coaching/list', { params });
}

export function createCoachingPost(data) {
    return api.post('/coaching', data);
}

// ============ Text Tutorial APIs ============

export function getTextTutorials(category) {
    const params = {};
    if (category) params.category = category;
    return api.get('/text-tutorial/list', { params });
}

export function getTextTutorialCategories() {
    return api.get('/text-tutorial/categories');
}

export function createTextTutorial(data) {
    return api.post('/text-tutorial', data);
}

// ============ Replay APIs ============

export function getReplays(category) {
    const params = {};
    if (category) params.category = category;
    return api.get('/replay/list', { params });
}

export function getTutorialCategories() {
    return api.get('/tutorial/categories');
}

// ============ Clan APIs ============

export function getClanRanking(query = '', sortBy = 'avgRating', region = 'ALL', offset = 0, limit = null) {
    const params = { sortBy };
    if (query) params.query = query;
    if (region && region !== 'ALL') params.region = region;
    if (offset) params.offset = offset;
    if (limit) params.limit = limit;
    return api.get('/clan/ranking', { params });
}

export function getClanRecruitments() {
    return api.get('/clan/recruitment');
}

export function createClanRecruitment(data) {
    return api.post('/clan/recruitment', data);
}

export function deleteClanRecruitment(id, userId) {
    return api.delete(`/clan/recruitment/${id}`, { params: { userId } });
}

// ============ Admin APIs ============

export function adminListUsers(adminId) {
    return api.get('/admin/users', { params: { adminId } });
}

export function adminUpdateUser(id, data, adminId) {
    return api.put(`/admin/users/${id}`, data, { params: { adminId } });
}
export function adminDeleteUser(id, adminId) {
    return api.delete(`/admin/users/${id}`, { params: { adminId } });
}

export function adminSetRole(id, role, adminId) {
    return api.put(`/admin/users/${id}/role`, null, { params: { role, adminId } });
}

export function adminListCheaters(adminId) {
    return api.get('/admin/cheaters', { params: { adminId } });
}

export function adminApproveCheater(id, adminId) {
    return api.put(`/admin/cheaters/${id}/approve`, null, { params: { adminId } });
}

export function adminRejectCheater(id, adminId) {
    return api.put(`/admin/cheaters/${id}/reject`, null, { params: { adminId } });
}

export function adminDeleteCheater(id, adminId) {
    return api.delete(`/admin/cheaters/${id}`, { params: { adminId } });
}
export function adminUpdateCheater(id, data, adminId) {
    return api.put(`/admin/cheaters/${id}`, data, { params: { adminId } });
}

export function adminListEvents(adminId) {
    return api.get('/admin/events', { params: { adminId } });
}

export function adminApproveEvent(id, adminId) {
    return api.put(`/admin/events/${id}/approve`, null, { params: { adminId } });
}

export function adminRejectEvent(id, adminId) {
    return api.put(`/admin/events/${id}/reject`, null, { params: { adminId } });
}

export function adminDeleteEvent(id, adminId) {
    return api.delete(`/admin/events/${id}`, { params: { adminId } });
}
export function adminUpdateEvent(id, data, adminId) {
    return api.put(`/admin/events/${id}`, data, { params: { adminId } });
}

export function adminListTutorials(adminId) {
    return api.get('/admin/tutorials', { params: { adminId } });
}

export function adminCreateTutorial(data, adminId) {
    return api.post('/admin/tutorials', data, { params: { adminId } });
}

export function adminDeleteTutorial(id, adminId) {
    return api.delete(`/admin/tutorials/${id}`, { params: { adminId } });
}
export function adminUpdateTutorial(id, data, adminId) {
    return api.put(`/admin/tutorials/${id}`, data, { params: { adminId } });
}

// --- Streams ---
export function adminListStreams(adminId) {
    return api.get('/admin/streams', { params: { adminId } });
}
export function adminDeleteStream(id, adminId) {
    return api.delete(`/admin/streams/${id}`, { params: { adminId } });
}
export function adminUpdateStream(id, data, adminId) {
    return api.put(`/admin/streams/${id}`, data, { params: { adminId } });
}

// --- Clan Recruitments ---
export function adminListClanRecruitments(adminId) {
    return api.get('/admin/clan-recruitments', { params: { adminId } });
}
export function adminDeleteClanRecruitment(id, adminId) {
    return api.delete(`/admin/clan-recruitments/${id}`, { params: { adminId } });
}
export function adminUpdateClanRecruitment(id, data, adminId) {
    return api.put(`/admin/clan-recruitments/${id}`, data, { params: { adminId } });
}

// --- Coaching Posts ---
export function adminListCoachingPosts(adminId) {
    return api.get('/admin/coaching-posts', { params: { adminId } });
}
export function adminDeleteCoachingPost(id, adminId) {
    return api.delete(`/admin/coaching-posts/${id}`, { params: { adminId } });
}
export function adminUpdateCoachingPost(id, data, adminId) {
    return api.put(`/admin/coaching-posts/${id}`, data, { params: { adminId } });
}

// --- Public Reports ---
export function adminListPublicReports(adminId) {
    return api.get('/admin/public-reports', { params: { adminId } });
}
export function adminDeletePublicReport(id, adminId) {
    return api.delete(`/admin/public-reports/${id}`, { params: { adminId } });
}
export function adminUpdatePublicReport(id, data, adminId) {
    return api.put(`/admin/public-reports/${id}`, data, { params: { adminId } });
}

// --- Text Tutorials ---
export function adminListTextTutorials(adminId) {
    return api.get('/admin/text-tutorials', { params: { adminId } });
}
export function adminDeleteTextTutorial(id, adminId) {
    return api.delete(`/admin/text-tutorials/${id}`, { params: { adminId } });
}
export function adminUpdateTextTutorial(id, data, adminId) {
    return api.put(`/admin/text-tutorials/${id}`, data, { params: { adminId } });
}

// --- Replays ---
export function adminListReplays(adminId) {
    return api.get('/admin/replays', { params: { adminId } });
}
export function adminDeleteReplay(id, adminId) {
    return api.delete(`/admin/replays/${id}`, { params: { adminId } });
}
export function adminUpdateReplay(id, data, adminId) {
    return api.put(`/admin/replays/${id}`, data, { params: { adminId } });
}

// --- Approval APIs ---
export function adminApproveTutorial(id, adminId) {
    return api.put(`/admin/tutorials/${id}/approve`, null, { params: { adminId } });
}
export function adminRejectTutorial(id, adminId) {
    return api.put(`/admin/tutorials/${id}/reject`, null, { params: { adminId } });
}
export function adminApproveTextTutorial(id, adminId) {
    return api.put(`/admin/text-tutorials/${id}/approve`, null, { params: { adminId } });
}
export function adminRejectTextTutorial(id, adminId) {
    return api.put(`/admin/text-tutorials/${id}/reject`, null, { params: { adminId } });
}
export function adminApproveCoachingPost(id, adminId) {
    return api.put(`/admin/coaching-posts/${id}/approve`, null, { params: { adminId } });
}
export function adminRejectCoachingPost(id, adminId) {
    return api.put(`/admin/coaching-posts/${id}/reject`, null, { params: { adminId } });
}
export function adminApproveReplay(id, adminId) {
    return api.put(`/admin/replays/${id}/approve`, null, { params: { adminId } });
}
export function adminRejectReplay(id, adminId) {
    return api.put(`/admin/replays/${id}/reject`, null, { params: { adminId } });
}

// --- Feedback management ---
export function adminListFeedbacks(adminId) {
    return api.get('/admin/feedbacks', { params: { adminId } });
}
export function adminUpdateFeedback(id, data, adminId) {
    return api.put(`/admin/feedbacks/${id}`, data, { params: { adminId } });
}
export function adminDeleteFeedback(id, adminId) {
    return api.delete(`/admin/feedbacks/${id}`, { params: { adminId } });
}

// --- QQ Groups ---
export function adminListQqGroups(adminId) {
    return api.get('/admin/qq-groups', { params: { adminId } });
}
export function adminDeleteQqGroup(id, adminId) {
    return api.delete(`/admin/qq-groups/${id}`, { params: { adminId } });
}
export function adminUpdateQqGroup(id, data, adminId) {
    return api.put(`/admin/qq-groups/${id}`, data, { params: { adminId } });
}
export function adminApproveQqGroup(id, adminId) {
    return api.put(`/admin/qq-groups/${id}/approve`, null, { params: { adminId } });
}
export function adminRejectQqGroup(id, adminId) {
    return api.put(`/admin/qq-groups/${id}/reject`, null, { params: { adminId } });
}

// --- Pending counts ---
export function adminGetPendingCounts(adminId) {
    return api.get('/admin/pending-counts', { params: { adminId } });
}

// ============ Auth helpers ============

export function saveUser(user) {
    // Backward compatibility:
    // - Old: saveUser(User)
    // - New: saveUser({ user: User, token: string })
    if (user && user.user && user.token) {
        localStorage.setItem(USER_KEY, JSON.stringify(user.user));
        localStorage.setItem(TOKEN_KEY, user.token);
        return;
    }
    localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getStoredUser() {
    const data = localStorage.getItem(USER_KEY);
    return data ? JSON.parse(data) : null;
}

export function getStoredToken() {
    return localStorage.getItem(TOKEN_KEY);
}

export function clearUser() {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
}

export default api;
